"use client";

import { FormEvent, useState } from "react";
import Link from "next/link";
import TurnstileWidget from "@/components/TurnstileWidget";

const MOTIVOS = [
  "Consulta general",
  "Quiero suscribirme / precios",
  "Soporte o problema",
  "Sugerencia",
  "Cervecería grande (plan a medida)",
  "Otro",
];

export default function ContactoPage() {
  const [motivo, setMotivo] = useState(MOTIVOS[0]);
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [cerveceria, setCerveceria] = useState("");
  const [telefono, setTelefono] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [tsToken, setTsToken] = useState<string | null>(null);
  const [tsKey, setTsKey] = useState(0);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!nombre.trim() || !email.includes("@") || !mensaje.trim()) {
      setError("Completá nombre, un email válido y el mensaje.");
      return;
    }
    if (!tsToken) {
      setError("Completá la verificación de seguridad.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          motivo, nombre, email, cerveceria, telefono, mensaje,
          turnstileToken: tsToken,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.error ?? "No se pudo enviar. Reintentá.");
        setTsToken(null);
        setTsKey((k) => k + 1);
        return;
      }
      setSent(true);
    } catch {
      setError("No se pudo enviar. Reintentá.");
      setTsToken(null);
      setTsKey((k) => k + 1);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-12">
      <div className="fixed top-4 left-4">
        <Link
          href="/"
          className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 border border-gray-200 hover:border-gray-400 bg-white rounded-lg px-3 py-1.5 transition-colors"
        >
          ← Inicio
        </Link>
      </div>

      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <div className="text-4xl mb-2">🍺</div>
          <h1 className="text-2xl font-bold text-gray-900">Escribinos</h1>
          <p className="text-gray-500 mt-1 text-sm">Contanos qué necesitás y te respondemos.</p>
        </div>

        {sent ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center space-y-4">
            <div className="text-4xl">✅</div>
            <h2 className="text-lg font-semibold text-gray-800">¡Mensaje enviado!</h2>
            <p className="text-sm text-gray-500">
              Gracias, {nombre.trim() || "che"}. Te vamos a responder a <strong>{email}</strong> a la brevedad.
            </p>
            <Link
              href="/"
              className="inline-block rounded-xl bg-amber-500 px-6 py-3 text-sm font-semibold text-white hover:bg-amber-600"
            >
              Volver al inicio
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
              <select
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                {MOTIVOS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  placeholder="Tu nombre"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  placeholder="tu@email.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cervecería <span className="text-gray-400">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={cerveceria}
                  onChange={(e) => setCerveceria(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  placeholder="Nombre de tu cervecería"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono <span className="text-gray-400">(opcional)</span>
                </label>
                <input
                  type="tel"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  placeholder="Ej: +54 9 264 ..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mensaje *</label>
              <textarea
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent resize-y"
                placeholder="Contanos en qué te podemos ayudar…"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="flex justify-center pt-1">
              <TurnstileWidget key={tsKey} onToken={setTsToken} onExpire={() => setTsToken(null)} />
            </div>

            <button
              type="submit"
              disabled={sending}
              className="w-full bg-amber-500 hover:bg-amber-600 active:bg-amber-700 text-white py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Enviando…" : "Enviar mensaje →"}
            </button>

            <p className="text-center text-xs text-gray-400">
              También podés escribirnos a{" "}
              <a href="mailto:hola@cuadernocervecero.com" className="text-amber-600 hover:underline">
                hola@cuadernocervecero.com
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
